import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Button, ButtonGroup, Checkbox } from '@chakra-ui/react';

import { Modal, Textarea } from 'shared-components';
import { IMessage } from 'types';

import { getDisplayName } from '../../../../utilities/formatting';
import { ApplicationState } from '../../../../store';
import { UNI_ROLES, ENROLY_AGENT_ROLES, AGENT_ROLES, UserRole } from '../../../../store/auth/types';
import { useTranslation } from "react-i18next";

export interface IProps {
  interviewName?: string;
  userRole: UserRole;
  onSendMessage: (message: IMessage) => void;
  onCancelRequest: (note: string) => void;
  onClose: () => void;
}

const CancelRecordedInterview = (props: IProps) => {
    const { t } = useTranslation('dialogs');
  const { interviewName, userRole, onSendMessage, onCancelRequest, onClose } = props;

  const { auth, config } = useSelector(({ auth, config }: ApplicationState) => ({ auth, config }), shallowEqual);

  const [message, setMessage] = useState('');
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  const cancelRequest = () => {
    if (!cancelConfirmed) {
      return;
    }

    onCancelRequest(message);

    const userName = auth.student_number! || 'Anonymous';
    const displayName = getDisplayName(auth, config);

    const msgReason = message ? `: ${message}` : '';

    const newMessage: IMessage = {
      isSendToStudent: false,
      messageType: 'note',
      author: userName,
      authorName: displayName || userName,
      messageFrom: 'uni',
      messageTo: 'uni',
      message: `Interview ${interviewName ? `<${interviewName}>` : ''} Cancelled${msgReason}`,
      createdAt: Date.now(),
      ...(UNI_ROLES.includes(userRole) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(userRole) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(userRole) ? { agentRead: true } : {}),
    };
    onSendMessage(newMessage);

    onClose();
  };

  return (
    <Modal
      id="cancel-recorded-interview"
      show={true}
      title={t('dialogs:cancelRecordedInterview')}
      onClose={onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          <Button onClick={onClose}>Don't cancel</Button>

          <Button id="cancel-interview-button" onClick={cancelRequest} isDisabled={!cancelConfirmed} variant={'solid'}>
            Confirm cancellation
          </Button>
        </ButtonGroup>
      }
    >
      <Textarea
        id={'cancel-reason-message'}
        label={t('dialogs:cancellationReason')}
        value={message}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) => setMessage(event.currentTarget.value)}
        placeholder={t('dialogs:typeYourMessageHere')}
        mb={6}
      />

      <Checkbox
        id="confirm-cancel-action"
        isChecked={cancelConfirmed}
        onChange={() => setCancelConfirmed(!cancelConfirmed)}
        size={'lg'}
      >
        {t('dialogs:iUnderstandThatThisInterviewWill')}
                    </Checkbox>
    </Modal>
  );
};

export default CancelRecordedInterview;
